import { computed, readonly, ref, type Ref } from 'vue'
import {
  useFormSubmitState,
  type MinimalFormApi,
  type UseFormSubmitStateOptions
} from './useFormSubmitState'
import { focusFirstErroredField } from './focus-first-errored-field'

/**
 * Options for `useFormSubmit`. All optional — the common case passes
 * nothing. Extends `UseFormSubmitStateOptions` so callers can tune the
 * disabled wiring (`requireDirty`, `reasons`) without layering a second
 * composable alongside.
 */
export interface UseFormSubmitOptions extends UseFormSubmitStateOptions {
  /**
   * Convert a thrown error into the display text. Defaults to
   * `err.message ?? String(err)`. Override when you need to unwrap a
   * specific error shape (e.g. an HTTP error with a nested detail).
   */
  errorMessageFromException?: (err: unknown) => string
}

export interface UseFormSubmitReturn {
  /**
   * Runs `form.handleSubmit()`. If the form's `onSubmit` throws, the
   * exception is captured into `errorMessage`. If validation fails, the
   * first errored field is focused. Callers don't need a try/catch.
   */
  submit: () => Promise<void>
  /** True while `handleSubmit` / `onSubmit` is in flight. */
  isSubmitting: Readonly<Ref<boolean>>
  /** Captured exception message, if any. Cleared on the next `submit()`. */
  errorMessage: Readonly<Ref<string | undefined>>
  /** Imperative setter — parent modals can push errors here on demand. */
  setErrorMessage: (msg: string | undefined) => void
  /** Convenience clear — equivalent to `setErrorMessage(undefined)`. */
  clearErrorMessage: () => void
  /** Combined disabled flag (mirrors `useFormSubmitState.disabled`). */
  disabled: Readonly<Ref<boolean>>
  /** Reason the submit is disabled — for a tooltip on the disabled button. */
  disabledReason: Readonly<Ref<string | undefined>>
}

/**
 * The canonical v2 submit-flow composable.
 *
 * Wraps tanstack's native `useForm({ onSubmit })` with three things every
 * v2 form needed: exception-to-errorMessage capture, focus-first-errored
 * -field on validation failure, and integration with `useFormSubmitState`
 * for disabled / submitting state.
 *
 * The caller writes their API call inside `useForm({ onSubmit })`,
 * throwing exceptions on failure. `useFormSubmit` does the rest.
 *
 * Usage:
 * ```ts
 * const form = useForm({
 *   defaultValues: { name: '' },
 *   validators: { onSubmit: schema },
 *   onSubmit: async ({ value }) => {
 *     await addStakeholderRequest(value)  // throws on failure
 *     emit('saved')
 *   }
 * })
 *
 * const { submit, errorMessage, disabled, isSubmitting } = useFormSubmit(form)
 *
 * defineExpose({ submit, errorMessage, disabled, isSubmitting })
 * ```
 *
 * The parent modal reads via template ref:
 * ```ts
 * const formRef = useTemplateRef('myForm')
 * const okButtonProps = reactive({
 *   text: 'Save', disabled: true, handleClick: () => formRef.value?.submit()
 * })
 * watchEffect(() => {
 *   okButtonProps.disabled = formRef.value?.disabled.value ?? true
 * })
 * ```
 */
export function useFormSubmit(
  form: MinimalFormApi,
  options: UseFormSubmitOptions = {}
): UseFormSubmitReturn {
  const errorMessageFromException =
    options.errorMessageFromException ?? defaultErrorMessageFromException

  // Compose with useFormSubmitState so callers get the same disabled /
  // disabledReason / isSubmitting they'd get from wiring it manually —
  // no need to compose both composables separately. `requireDirty` and
  // `reasons` from the caller's options pass straight through.
  const submitState = useFormSubmitState(form, {
    requireDirty: options.requireDirty,
    reasons: options.reasons
  })

  const errorMessage = ref<string | undefined>(undefined)

  // Guard against re-entrant submit calls. Although `disabled` should
  // prevent the button from firing twice, some callers may invoke
  // `submit()` programmatically (e.g. from a keyboard shortcut) — this
  // keeps semantics safe under those paths too.
  let inFlight = false

  async function submit(): Promise<void> {
    if (inFlight) {
      console.debug(
        '[useFormSubmit] submit() called while already in flight — dropped'
      )
      return
    }
    inFlight = true
    errorMessage.value = undefined
    try {
      // Flush any pending debounced `onChangeAsync` validators before
      // calling `handleSubmit`. Without this, Save clicked during the
      // debounce window would race tanstack's internal `canSubmit`
      // check — the async fires during `handleSubmit`'s awaits,
      // `isValidating` flips, and `handleSubmit` bails. Forcing the
      // change-phase validators to run first synchronously updates the
      // field state so `handleSubmit` sees a settled view.
      if (form.validateAllFields) {
        await form.validateAllFields('change')
      }
      // `handleSubmit` runs validators, and if valid, calls the user's
      // `onSubmit`. If the user's onSubmit throws, handleSubmit rejects
      // with the same exception. If validation fails, handleSubmit
      // resolves without calling onSubmit.
      await form.handleSubmit()
    } catch (err) {
      errorMessage.value = errorMessageFromException(err)
      return
    } finally {
      inFlight = false
    }
    // Post-resolve: if the form still has field errors, validation must
    // have failed — focus the first invalid field so the user jumps
    // straight to it.
    if (hasFieldErrors(form)) {
      focusFirstErroredField(formWithFieldMeta(form))
    }
  }

  function setErrorMessage(msg: string | undefined): void {
    errorMessage.value = msg
  }

  function clearErrorMessage(): void {
    errorMessage.value = undefined
  }

  // `disabled` intentionally reflects only `isSubmitting` — never the
  // pristine/invalid state. The UX we want is: button is always
  // clickable, and clicking runs validation; invalid submits show
  // inline errors and focus-first-errored-field. Disabling on pristine
  // or invalid hides the affordance and forces users to guess why the
  // button is grey. Submitting is the exception because a second click
  // during in-flight submit is always a no-op (guarded by the inFlight
  // check) and visually disabling during the call prevents re-click
  // flicker.
  //
  // `requireDirty` and `reasons` options are still accepted and still
  // affect `submitState.disabledReason`, so consumers that wire
  // tooltips manually can read a specific reason — but they no longer
  // affect the button's enabled state.
  const disabled = submitState.isSubmitting
  const disabledReason = computed<string | undefined>(() =>
    submitState.isSubmitting.value
      ? (submitState.disabledReason.value ?? 'Submitting…')
      : undefined
  )

  return {
    submit,
    isSubmitting: submitState.isSubmitting,
    errorMessage: readonly(errorMessage),
    setErrorMessage,
    clearErrorMessage,
    disabled,
    disabledReason
  }
}

function defaultErrorMessageFromException(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  if (typeof err === 'string') return err
  return String(err)
}

/**
 * Check if the tanstack form has any field-level errors currently. Used
 * to distinguish "valid + onSubmit ran cleanly" from "invalid + onSubmit
 * was not called" after `handleSubmit` resolves.
 */
function hasFieldErrors(form: MinimalFormApi): boolean {
  const state = (
    form as unknown as {
      state: { fieldMeta?: Record<string, { errors?: unknown[] } | undefined> }
    }
  ).state
  const meta = state.fieldMeta
  if (!meta) return false
  for (const fieldMeta of Object.values(meta)) {
    if (fieldMeta?.errors && fieldMeta.errors.length > 0) return true
  }
  return false
}

/**
 * Narrow the loose `MinimalFormApi` into the exact shape
 * `focusFirstErroredField` needs. Internal cast — avoids leaking the
 * `unknown as` pattern into every call site.
 */
function formWithFieldMeta(form: MinimalFormApi): {
  state: { fieldMeta?: Record<string, { errors?: unknown[] } | undefined> }
} {
  return form as unknown as {
    state: { fieldMeta?: Record<string, { errors?: unknown[] } | undefined> }
  }
}
